import {
  ICreateProfileNoteTagBody,
  ICreateProfileNoteBody,
  ICreateProfileNoteReplyBody,
  IProfileNote,
  IProfileNoteTag,
  IUpdateProfileNoteBody,
} from 'types/profileInterfaces'
import { SubAPI } from './API'

/**
 * Profile Notes API
 * @notExported
 */
class ProfileNotesAPI extends SubAPI {
  /**
   * Get profile notes for a person.
   *
   * @param personId - Profile ID that notes are for.
   * @param controller - AbortController.
   * @returns List of notes.
   */
  public getNotes(personId: number, controller?: AbortController): Promise<IProfileNote[]> {
    return this.api.get(`profile-notes/${personId}`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get available tags for profile notes in the person's organization.
   *
   * @param personId - Profile ID that tags are resolved from.
   * @param controller - AbortController.
   * @returns List of tags.
   */
  public getTags(personId: number, controller?: AbortController): Promise<IProfileNoteTag[]> {
    return this.api.get(`profile-notes/${personId}/tags`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Create a profile note for a person.
   *
   * @param personId - Profile ID that note is for.
   * @param payload - Note data.
   * @param controller - AbortController.
   * @returns Created note.
   */
  public createNote(
    personId: number,
    payload: ICreateProfileNoteBody,
    controller?: AbortController
  ): Promise<IProfileNote> {
    return this.api.post(`profile-notes/${personId}`, payload, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Create a new profile note tag in the person's organization.
   *
   * @param personId - Profile ID that determines organization context.
   * @param payload - Tag data.
   * @param controller - AbortController.
   * @returns Created tag.
   */
  public createTag(
    personId: number,
    payload: ICreateProfileNoteTagBody,
    controller?: AbortController
  ): Promise<IProfileNoteTag> {
    return this.api.post(
      `profile-notes/${personId}/tags`,
      payload,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Update a profile note.
   *
   * @param personId - Profile ID that note is for.
   * @param noteId - Note ID that is being updated.
   * @param payload - Note data to update.
   * @param controller - AbortController.
   * @returns Updated note.
   */
  public updateNote(
    personId: number,
    noteId: number,
    payload: IUpdateProfileNoteBody,
    controller?: AbortController
  ): Promise<IProfileNote> {
    return this.api.put(
      `profile-notes/${personId}/${noteId}`,
      payload,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Reply to a profile note.
   *
   * @param personId - Profile ID that note is for.
   * @param noteId - Note ID that is being replied to.
   * @param payload - Reply data.
   * @param controller - AbortController.
   * @returns Created reply.
   */
  public replyToNote(
    personId: number,
    noteId: number,
    payload: ICreateProfileNoteReplyBody,
    controller?: AbortController
  ): Promise<IProfileNote> {
    return this.api.post(
      `profile-notes/${personId}/${noteId}/replies`,
      payload,
      controller ? { signal: controller.signal } : undefined
    )
  }
}

export const profileNotesAPI = new ProfileNotesAPI()
